import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Plus, Pencil, Trash2, Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_WEAK_PASSWORD,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WeakPasswordForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_WEAK_PASSWORD;

/** Action policies. Only "Block" is confirmed from a captured payload; Redirect /
 *  By-pass values are best-effort and may need adjusting against the portal. */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Redirect', label: 'Redirect' },
  { value: 'By-pass', label: 'By-pass' },
] as const;

/** One Triggering Condition (a login path + its username/password params). */
interface PathLimit {
  path: string;
  user_param: string;
  pwd_param: string;
}

/** The Weak Password Validation (type 6) per-type config. */
interface WeakPasswordConfig {
  path_limit: PathLimit[];
  action: string;
  block_value: number;
  delay: number;
  redirect_value: string;
}

interface FormState extends WeakPasswordConfig {
  name: string;
  description: string;
  sites: number[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  path_limit: [],
  action: 'Block',
  block_value: 418,
  delay: 0,
  redirect_value: '',
  sites: [],
});

const emptyCond = { path: '', user_param: '', pwd_param: '' };

/**
 * Add / Edit a Weak Password Validation rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 6). Like Brute Force without the frequency / Auto-Web
 * ACL: path_limit entries are {path, user_param, pwd_param}. Builds the per-type
 * `config`; the BE adds the common envelope keys. Routes:
 * aiwaf/advanced/weak-password/new + /:id.
 */
export function WeakPasswordForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<WeakPasswordConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as WeakPasswordConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      path_limit: Array.isArray(c.path_limit) ? c.path_limit : [],
      action: c.action || 'Block',
      block_value: c.block_value ?? 418,
      delay: c.delay ?? 0,
      redirect_value: c.redirect_value ?? '',
      sites: existing.sites ?? [],
    });
  }, [existing]);

  const [cond, setCond] = useState(emptyCond);
  const addCond = () => {
    if (!cond.path.trim()) return;
    set('path_limit', [...form.path_limit, { ...cond, path: cond.path.trim() }]);
    setCond(emptyCond);
  };
  const removeCond = (i: number) => set('path_limit', form.path_limit.filter((_, idx) => idx !== i));
  const editCond = (i: number) => {
    setCond({ ...form.path_limit[i] });
    removeCond(i);
  };

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<WeakPasswordConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'weak_password_rule', 'create');
      show({ tone: 'success', title: 'Weak Password rule created' });
      navigate(`${base}/aiwaf/advanced?tab=weak-password`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<WeakPasswordConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'weak_password_rule', 'edit');
      show({ tone: 'success', title: 'Weak Password rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=weak-password`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit = nameValid && form.path_limit.length > 0 && actionValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<WeakPasswordConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        path_limit: form.path_limit,
        action: form.action,
        block_value: form.block_value,
        delay: form.delay,
        redirect_value: form.action === 'Redirect' ? form.redirect_value.trim() : '',
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Weak Password Rule' : 'Weak Password Validation Rule' },
            ]}
          />
        }
        title="Weak Password Validation Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
              <CardDescription>Validate login requests on these paths for weak passwords.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.condRow}>
              <Field label="Path" required>
                {(props) => (
                  <Input
                    {...props}
                    value={cond.path}
                    maxLength={512}
                    placeholder="/login"
                    className={styles.pathInput}
                    onChange={(e) => setCond((d) => ({ ...d, path: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Username Parameter">
                {(props) => (
                  <Input
                    {...props}
                    value={cond.user_param}
                    placeholder="username"
                    className={styles.paramInput}
                    onChange={(e) => setCond((d) => ({ ...d, user_param: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Password Parameter">
                {(props) => (
                  <Input
                    {...props}
                    value={cond.pwd_param}
                    placeholder="password"
                    className={styles.paramInput}
                    onChange={(e) => setCond((d) => ({ ...d, pwd_param: e.target.value }))}
                  />
                )}
              </Field>
              <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addCond}>
                Add
              </Button>
            </div>

            <PathLimitTable rows={form.path_limit} onEdit={editCond} onRemove={removeCond} />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Configure how requests that satisfy the triggering condition are handled.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className={styles.actionRow}>
                <Field label="Delay (second(s))" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={0}
                      value={String(form.delay)}
                      className={styles.numField}
                      onChange={(e) => set('delay', Number(e.target.value))}
                    />
                  )}
                </Field>
                {form.action === 'Block' && (
                  <Field label="Response Status Code" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={100}
                        max={599}
                        value={String(form.block_value)}
                        className={styles.numField}
                        onChange={(e) => set('block_value', Number(e.target.value))}
                      />
                    )}
                  </Field>
                )}
                {form.action === 'Redirect' && (
                  <Field label="Redirect Path" required>
                    {(props) => (
                      <Input
                        {...props}
                        value={form.redirect_value}
                        placeholder="/blocked"
                        className={styles.redirectInput}
                        onChange={(e) => set('redirect_value', e.target.value)}
                      />
                    )}
                  </Field>
                )}
              </div>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=weak-password`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}

/** Triggering-condition table — Path / Username / Password + Edit/Delete. */
function PathLimitTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: PathLimit[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  const cols = '2fr 1.5fr 1.5fr auto';
  return (
    <div className={styles.table}>
      <div className={styles.tableHead} style={{ gridTemplateColumns: cols }}>
        <div className={styles.cell}>Path</div>
        <div className={styles.cell}>Username Parameter</div>
        <div className={styles.cell}>Password Parameter</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={20} />} title="No path limits yet" description="Add triggering conditions to define where rate limiting applies." />
      ) : (
        rows.map((r, i) => (
          <div key={i} className={styles.tableRow} style={{ gridTemplateColumns: cols }}>
            <div className={styles.cell}>{r.path}</div>
            <div className={styles.cell}>{r.user_param || '—'}</div>
            <div className={styles.cell}>{r.pwd_param || '—'}</div>
            <div className={`${styles.cell} ${styles.cellActions}`}>
              <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
                <Pencil size={16} />
              </IconButton>
              <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
